import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { AuthUser } from "../../types/api.js";
import { ApiClientError } from "../../services/api-client.js";
import * as authApi from "../../services/auth-api.js";
import { fetchLicenses } from "../../services/licenses-api.js";
import {
  clearAuthStorage,
  getStoredRefreshToken,
  getStoredToken,
  setStoredAccessExpiresAtMs,
  setStoredRefreshToken,
  setStoredToken,
} from "../../lib/storage-token.js";
import { clearAuthSessionBridge, setAuthSessionBridge } from "../../lib/auth-session-bridge.js";

type AuthStatus = "idle" | "loading" | "ready" | "error";

/** Roles de plataforma: bypass de restricciones de módulo */
const PLATFORM_BYPASS_ROLES = new Set(["superadmin", "platform-support"]);

export interface AuthContextValue {
  token: string | null;
  user: AuthUser | null;
  status: AuthStatus;
  bootstrapped: boolean;
  authError: string | null;
  flash: string | null;
  /** Módulos con licencia para el tenant actual. Vacío = cargando/desconocido. */
  licensedModules: string[];
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  setFlash: (msg: string | null) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [token, setToken] = useState<string | null>(() => getStoredToken());
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>("idle");
  const [authError, setAuthError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [bootstrapped, setBootstrapped] = useState(false);
  const [licensedModules, setLicensedModules] = useState<string[]>([]);

  /** Carga los módulos licenciados del tenant actual */
  const loadLicensedModules = useCallback(async (t: string, u: AuthUser) => {
    if (!u.tenantId) {
      /* Roles de plataforma sin tenant tienen acceso a todo; usar array especial */
      if (PLATFORM_BYPASS_ROLES.has(u.role)) {
        setLicensedModules(["*"]);
      } else {
        setLicensedModules([]);
      }
      return;
    }
    if (PLATFORM_BYPASS_ROLES.has(u.role)) {
      setLicensedModules(["*"]);
      return;
    }
    try {
      const { licenses } = await fetchLicenses(t);
      const modules = new Set<string>();
      for (const lic of licenses) {
        for (const m of lic.modules) {
          modules.add(m);
        }
      }
      setLicensedModules(Array.from(modules));
    } catch {
      /* Si falla (403 u otro), dejamos vacío — la API enforcement bloqueará igualmente */
      setLicensedModules([]);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    const t = getStoredToken();
    if (!t) {
      setUser(null);
      setToken(null);
      setLicensedModules([]);
      setStatus("ready");
      return;
    }
    setToken(t);
    setStatus("loading");
    setAuthError(null);
    try {
      const { user: u } = await authApi.meRequest(t);
      setUser(u);
      setStatus("ready");
      void loadLicensedModules(t, u);
    } catch (e) {
      clearAuthStorage();
      setToken(null);
      setUser(null);
      setLicensedModules([]);
      setStatus("error");
      const msg = e instanceof ApiClientError ? e.message : "Sesión inválida.";
      setAuthError(msg);
    }
  }, [loadLicensedModules]);

  useEffect(() => {
    void refreshUser().finally(() => setBootstrapped(true));
  }, [refreshUser]);

  useEffect(() => {
    setAuthSessionBridge({
      onTokensRefreshed: (access) => {
        setToken(access);
      },
      onSessionInvalidated: () => {
        setToken(null);
        setUser(null);
        setStatus("ready");
        setAuthError("Sesión expirada. Vuelva a iniciar sesión.");
      },
    });
    return () => clearAuthSessionBridge();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setAuthError(null);
    setStatus("loading");
    try {
      const res = await authApi.loginRequest(email, password);
      const access = res.accessToken ?? res.token;
      setStoredToken(access);
      setStoredRefreshToken(res.refreshToken);
      if (typeof res.expiresIn === "number" && res.expiresIn > 0) {
        setStoredAccessExpiresAtMs(Date.now() + res.expiresIn * 1000);
      }
      setToken(access);
      setUser(res.user);
      setStatus("ready");
      setFlash("Sesión iniciada.");
      void loadLicensedModules(access, res.user);
    } catch (e: unknown) {
      setStatus("ready");
      const msg = e instanceof ApiClientError ? e.message : "No se pudo iniciar sesión.";
      setAuthError(msg);
      throw e;
    }
  }, [loadLicensedModules]);

  const logout = useCallback(() => {
    const rt = getStoredRefreshToken();
    clearAuthStorage();
    setToken(null);
    setUser(null);
    setLicensedModules([]);
    setStatus("ready");
    setAuthError(null);
    setFlash("Sesión cerrada.");
    if (rt) {
      void authApi.logoutRequest(rt).catch(() => undefined);
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      token,
      user,
      status,
      bootstrapped,
      authError,
      flash,
      licensedModules,
      login,
      logout,
      refreshUser,
      setFlash,
    }),
    [token, user, status, bootstrapped, authError, flash, licensedModules, login, logout, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth debe usarse dentro de AuthProvider");
  return ctx;
}

/**
 * Devuelve true si el tenant tiene el módulo con licencia, o si el usuario
 * es de plataforma (superadmin / platform-support → acceso a todo).
 */
export function useHasModule(module: string): boolean {
  const { licensedModules } = useAuth();
  /* "*" = bypass completo (roles de plataforma) */
  if (licensedModules.includes("*")) return true;
  /* "core" en licencia = acceso completo también */
  if (licensedModules.includes("core")) return true;
  return licensedModules.includes(module);
}
